import type { JSX } from "react";
import { PeopleRegular, SearchRegular } from "@fluentui/react-icons";
import type { DealPersonOption } from "./create-deal-helpers";
import { filterRoster } from "./create-deal-helpers";
import { ownerCandidates, sortDealTeam, type DealTeamSelection } from "./deal-team";

export interface DealTeamProps {
  options: DealPersonOption[];
  selection: DealTeamSelection;
  search: string;
  onSearchChange: (value: string) => void;
  onToggleMember: (key: string) => void;
  onToggleOwner: (key: string) => void;
  onToggleAll: (checked: boolean) => void;
  isMultiOwner?: boolean;
  ownerSelectionLocked?: boolean;
  memberSelectionDisabled?: boolean;
  lockedMemberKeys?: ReadonlySet<string>;
  ownerSelectionDisabled?: boolean;
  showOwnerSelection?: boolean;
  disabled?: boolean;
  /** Shown when the last owner or the last member would be removed. */
  notice?: string;
  error?: string;
}

/**
 * The Deal team picker, modelled on Triyo.Web's project member selection: one
 * combined list of people and groups (the members), with the owners chosen from
 * the members that are already selected. Groups can be members but not owners.
 */
export function DealTeam({
  options,
  selection,
  search,
  onSearchChange,
  onToggleMember,
  onToggleOwner,
  onToggleAll,
  isMultiOwner = true,
  ownerSelectionLocked = false,
  memberSelectionDisabled = false,
  lockedMemberKeys,
  ownerSelectionDisabled = false,
  showOwnerSelection = true,
  disabled = false,
  notice,
  error,
}: DealTeamProps): JSX.Element {
  const sorted = sortDealTeam(options, selection);
  const visible = filterRoster(sorted, search);
  const owners = ownerCandidates(sorted, selection);
  const allSelected = options.length > 0 && selection.memberKeys.size === options.length;

  return (
    <div className="create-deal__team">
      <p className="create-deal__step-hint">
        {showOwnerSelection
          ? `Select everyone working on this Deal, then choose ${isMultiOwner ? "which of them own it" : "its owner"}.`
          : "Review or update everyone working on this Deal."}
      </p>

      <div className="create-deal__search">
        <SearchRegular />
        <input
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search members and groups"
          aria-label="Search members and groups"
        />
      </div>

      {/* Triyo.Web only offers Select All on the unfiltered list. */}
      {search.trim() === "" && options.length > 0 ? (
        <label className="create-deal__select-all">
          <input
            type="checkbox"
            checked={allSelected}
            disabled={disabled || memberSelectionDisabled}
            onChange={(event) => onToggleAll(event.target.checked)}
          />
          <span>Select all</span>
        </label>
      ) : null}

      {visible.length === 0 ? (
        <p className="create-deal__no-matches">No matches</p>
      ) : (
        <div className="create-deal__roster" role="group" aria-label="Deal members">
          {visible.map((person) => (
            <label key={person.key} className="create-deal__roster-row">
              <input
                type="checkbox"
                checked={selection.memberKeys.has(person.key)}
                disabled={
                  disabled || memberSelectionDisabled || person.unavailable || lockedMemberKeys?.has(person.key)
                }
                onChange={() => onToggleMember(person.key)}
              />
              <span className="create-deal__roster-text">
                <span className="create-deal__roster-name">
                  {person.isGroup ? <PeopleRegular className="create-deal__roster-group-icon" /> : null}
                  {person.name}
                  {selection.ownerKeys.has(person.key) ? <span className="create-deal__owner-tag">Owner</span> : null}
                  {person.unavailable ? <span className="create-deal__unavailable-tag">Unavailable</span> : null}
                </span>
                {/* Role and company share one line to keep rows compact. */}
                {person.detail || person.company ? (
                  <span className="create-deal__roster-detail">
                    {[person.detail, person.company].filter(Boolean).join(" · ")}
                  </span>
                ) : null}
              </span>
            </label>
          ))}
        </div>
      )}

      <p className="create-deal__selected-count">{selection.memberKeys.size} selected</p>

      {showOwnerSelection ? (
        <div className="create-deal__owners">
          <span className="create-deal__owners-label">
            {isMultiOwner ? "Owners" : "Owner"} <span className="create-deal__required">*</span>
          </span>
          {ownerSelectionLocked ? (
            <p className="create-deal__owners-empty">The template fixes the owner selection during Deal creation.</p>
          ) : null}
          {owners.length === 0 ? (
            <p className="create-deal__owners-empty">Select a member above to choose an owner.</p>
          ) : (
            <div className="create-deal__owners-list" role="group" aria-label="Deal owners">
              {owners.map((person) => (
                <label key={person.key} className="create-deal__owner-row">
                  <input
                    type={isMultiOwner ? "checkbox" : "radio"}
                    name={isMultiOwner ? undefined : "create-deal-owner"}
                    checked={selection.ownerKeys.has(person.key)}
                    onChange={() => onToggleOwner(person.key)}
                    disabled={disabled || ownerSelectionDisabled || ownerSelectionLocked || person.unavailable}
                  />
                  <span>{person.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      ) : null}

      {notice ? (
        <p className="create-deal__team-notice" role="status">
          {notice}
        </p>
      ) : null}
      {error ? <p className="create-deal__field-error">{error}</p> : null}
    </div>
  );
}
